import { useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { useAsyncFn } from 'react-use';

import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';

import { useIsInteractive } from '../../hooks/useIsInteractive';
import { ControlButton, ControlButtonProps } from './ControlButton';

export type ResetButtonProps = ControlButtonProps & {
  onReset?: () => Promise<void>;
  showWhenNotInteractive?: boolean;
};

export const ResetButton = ({
  onClick,
  onReset,
  children,
  showWhenNotInteractive = false,
  ...props
}: ResetButtonProps) => {
  const [{ loading }, resetFn] = useAsyncFn(async () => onReset?.(), [onReset]);
  const { isInteractive, toggleIsInteractive } = useIsInteractive();

  const defaultOnClick: ControlButtonProps['onClick'] = async () => {
    if (onReset) {
      await resetFn();
    } else {
      toggleIsInteractive();
    }
  };

  if (!isInteractive && !showWhenNotInteractive) return null;
  return (
    <ControlButton
      variant="secondary"
      onClick={onClick ?? defaultOnClick}
      title="reset tile positions"
      aria-label="reset tile positions"
      loading={loading}
      startIcon={MagicWand01Icon}
      {...props}
    >
      {children ?? <>Auto position tiles</>}
    </ControlButton>
  );
};

export type ResetButtonMenuItemProps = Partial<MenuItemProps> & {
  onReset: () => Promise<void>;
};

export const ResetButtonMenuItem = ({
  onReset,
  ...props
}: ResetButtonMenuItemProps) => {
  const theme = useTheme();
  const timeout = theme.transitions.duration.leavingScreen;
  const [loading, setLoading] = useState(false);

  // this workaround is necessary because the MenuItem is still
  // active while the close animation is running -- and double-clicking
  // will cause the click handler to fire repeatedly until the diagram crashes
  const onClick = useCallback(() => {
    setLoading(true);
    void onReset();
    setTimeout(() => {
      setLoading(false);
    }, timeout);
  }, [onReset, timeout]);

  return (
    <MenuItem
      label="Automatically position tiles"
      onClick={onClick}
      loading={loading}
      {...props}
    />
  );
};
